import React from 'react';
import { useController } from 'react-hook-form';
import Input from '@amzn/meridian/input';
import Text from '@amzn/meridian/text';
import Loader from '@amzn/meridian/loader';
import Row from '@amzn/meridian/row';
import { useTranslation } from 'src/arb-wrapper';
import type { FormMeridianInputProps, FormRhfControllerProps } from './types';

interface FormInputProps extends FormMeridianInputProps, FormRhfControllerProps {
  trim?: boolean;
  validator?: {
    fn: () => boolean;
    message?: string;
  };
  isValidating?: boolean;
}

const MIN_LENGTH = 3;

/** `FormInput` is a Meridian `Input` supporting form validation. */
export const FormInput: React.FC<FormInputProps> = (props) => {
  const { control, name, trim, validator, isValidating, ...meridianProps } = props;

  const { t } = useTranslation();
  const { field, fieldState } = useController({
    control,
    name,
    rules: {
      required: meridianProps.required
        ? {
            value: true,
            message: t('form_validation_required_field'),
          }
        : undefined,
      minLength: {
        value: MIN_LENGTH,
        message: t('form_validation_min_length').replace(
          /%d/,
          MIN_LENGTH.toString()
        ),
      },
      validate: validator ? validator.fn : undefined,
    },
  });

  return (
    <Input
      {...meridianProps}
      error={!!fieldState.error}
      errorMessage={fieldState.error?.message?.length ? fieldState.error.message : !!fieldState.error && validator?.message}
      suffix={isValidating
        ? (
        <Row alignmentVertical="center" aria-label={t('form_validation_in_progress')} aria-live="polite" spacing="100">
          <Loader size="small" />
          <Text color="secondary" type="b100">{t('form_validation_in_progress')}</Text>
        </Row>
          )
        : undefined}
      value={field.value}
      onBlur={field.onBlur}
      onChange={trim ? (value?: string) => { field.onChange(value?.trim()); } : field.onChange}
    />
  );
};
